/**
 * Time formatting utilities for VRP system
 * Fixes critical UoM display bug where times show in seconds instead of minutes
 */

/**
 * Formats time in seconds to user-friendly display format
 * @param seconds - Time value in seconds
 * @param options - Formatting options
 * @returns Formatted time string with appropriate unit
 */
export function formatJobTime(
  seconds: number, 
  options: {
    showSeconds?: boolean;
    precision?: number;
    compact?: boolean;
  } = {}
): string {
  const { showSeconds = false, precision = 1, compact = false } = options;
  
  if (seconds === 0) {
    return compact ? '0 min' : '0 minutes';
  }
  
  // Convert seconds to minutes
  const minutes = seconds / 60;
  
  // For very short times (< 1 minute), show in seconds if requested
  if (showSeconds && seconds < 60) {
    return `${seconds}s`;
  }
  
  // For times >= 1 minute, always show in minutes
  if (minutes === Math.floor(minutes)) {
    // Whole minutes
    const unit = compact ? 'min' : (minutes === 1 ? 'minute' : 'minutes');
    return `${minutes} ${unit}`;
  } else {
    // Fractional minutes
    const unit = compact ? 'min' : 'minutes';
    return `${minutes.toFixed(precision)} ${unit}`;
  }
}

/**
 * Formats setup and service time pair for table display
 * @param setupSeconds - Setup time in seconds
 * @param serviceSeconds - Service time in seconds
 * @returns Object with formatted setup and service strings
 */
export function formatJobTimePair(setupSeconds: number, serviceSeconds: number) {
  return {
    setup: formatJobTime(setupSeconds, { compact: true }),
    service: formatJobTime(serviceSeconds, { compact: true }),
    setupLong: formatJobTime(setupSeconds),
    serviceLong: formatJobTime(serviceSeconds)
  };
}

/**
 * Gets appropriate form label for time input fields
 * @param fieldType - Type of time field
 * @returns User-friendly label with correct unit expectation
 */
export function getTimeFieldLabel(fieldType: 'setup' | 'service'): string {
  switch (fieldType) {
    case 'setup':
      return 'Setup Time (minutes)';
    case 'service':
      return 'Service Time (minutes)';
    default:
      return 'Time (minutes)';
  }
}

/**
 * Gets appropriate helper text for time input fields
 * @param fieldType - Type of time field
 * @returns Helper text explaining the field
 */
export function getTimeFieldHelperText(fieldType: 'setup' | 'service'): string {
  switch (fieldType) {
    case 'setup':
      return 'Time needed to prepare before service (in minutes)';
    case 'service':
      return 'Time needed to complete the job (in minutes)';
    default:
      return 'Enter time in minutes';
  }
}

/**
 * Converts minutes to seconds for storage
 * @param minutes - Time value in minutes
 * @returns Time value in seconds
 */
export function minutesToSeconds(minutes: number): number {
  return Math.round(minutes * 60);
}

/**
 * Converts seconds to minutes for display
 * @param seconds - Time value in seconds
 * @returns Time value in minutes
 */
export function secondsToMinutes(seconds: number): number {
  return seconds / 60;
}

/**
 * Formats duration in seconds to human-readable string
 * This is the missing function causing the time conversion bug
 * @param seconds - Duration in seconds
 * @returns Formatted duration string (e.g., "10 min", "1.5 hours")
 */
export function formatDuration(seconds: number): string {
  if (!seconds || seconds === 0 || isNaN(seconds)) {
    return '0 min';
  }
  
  // Convert seconds to minutes
  const minutes = seconds / 60;
  
  // For durations less than 60 minutes, show in minutes
  if (minutes < 60) {
    if (minutes === Math.floor(minutes)) {
      return `${minutes} min`;
    } else {
      return `${minutes.toFixed(1)} min`;
    }
  }
  
  // For durations 60+ minutes, show in hours
  const hours = minutes / 60;
  if (hours === Math.floor(hours)) {
    return `${hours} hour${hours !== 1 ? 's' : ''}`;
  } else {
    return `${hours.toFixed(1)} hours`;
  }
}